import React from 'react';

import {assert} from 'Asserts';
import {pluralize} from 'BenchlingUtil';
import {ProcedureType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {Cell} from 'notebook/tables/constants/CellConstants';
import {RowData} from 'notebook/tables/constants/RowConstants';
import {Table} from 'notebook/tables/constants/TableConstants';
import ErrorTriangle, {TriangleLocation, TriangleState} from 'notebook/tables/errors/ErrorTriangle';
import notifications from 'notifications';
import {ExperimentalInputs} from 'procedures/planning/ProcedurePlanTypes';
import {EXPERIMENT_VALIDATION_CONSTANTS} from 'studies/StudyConstants';
import {getProcedureRunDesignFlowchartNodeId} from 'studies/typed/shared/flowcharts/design/ProcedureRunDesignFlowchartNodeIdUtils';
import {
  BaseCondition,
  Condition,
  ConditionGroup,
  ConditionValidationState,
  getMethodIdsImplementingConditionGroup,
  isConditionGroup,
  isPersistedCondition,
} from 'studies/typed/shared/procedure-runs/design-state/useExperimentDesignState';
import {getProcedureRunConditionTextForProcedureType} from 'studies/TypedStudyUtils';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import {ButtonStyle} from 'ui-library/buttons/Button';
import {NEAR_BLACK} from 'ui-library/Colors';
import * as modals from 'util/Modals';
import {Optional} from 'util/Types';

import {getConditionGroupGridRows} from './conditionGroupGridRows';
import {
  buildDraftConditionGroupPmdvMap,
  buildValidationStatesForUpdate,
  ConditionDraft,
} from './conditionUtils';

/**
 * The subset of a table {@link Cell} these replicate helpers read. Text may be nullish at runtime
 * (blank or absent cells), unlike `Cell`'s stricter `text: string`.
 */
interface ReplicateCountCell {
  text: Optional<string>;
}

/** Live vs fully archived replicate counts for a condition. */
export interface ReplicateCounts {
  liveReplicateCount: number;
  archivedReplicateCount: number;
}

/** Counts a group's live and archived replicates. */
export function getReplicateCounts(group: ConditionGroup): ReplicateCounts {
  const allPersistedNumbers = new Set<number>();
  const unarchivedNumbers = new Set<number>();
  for (const cells of Object.values(group.replicatesByConditionId ?? {})) {
    for (const cell of cells) {
      allPersistedNumbers.add(cell.replicateNumber);
      if (!cell.archived) {
        unarchivedNumbers.add(cell.replicateNumber);
      }
    }
  }

  // A method covered without a persisted condition makes every persisted flow live.
  const hasMethodCoveredWithoutCondition = Object.values(group.conditionIdsByPmdvId).some(
    (conditionId) => conditionId == null
  );
  const livePersistedCount = hasMethodCoveredWithoutCondition
    ? allPersistedNumbers.size
    : unarchivedNumbers.size;

  // Appends the draft flows the flowchart adds when numReplicates exceeds the unarchived persisted ones.
  const addedDraftCount = Math.max(0, group.numReplicates - unarchivedNumbers.size);
  return {
    liveReplicateCount: livePersistedCount + addedDraftCount,
    archivedReplicateCount: hasMethodCoveredWithoutCondition
      ? 0
      : allPersistedNumbers.size - unarchivedNumbers.size,
  };
}

/** Archive display state for one condition row, treating non-group inputs as live. */
export interface ConditionArchiveState extends ReplicateCounts {
  isFullyArchived: boolean;
}

export function getConditionArchiveState(
  condition: BaseCondition,
  isArchiveDisplayEnabled: boolean
): ConditionArchiveState {
  if (!isArchiveDisplayEnabled || !isConditionGroup(condition)) {
    return {
      isFullyArchived: false,
      liveReplicateCount: condition.numReplicates,
      archivedReplicateCount: 0,
    };
  }
  const replicateCounts = getReplicateCounts(condition);
  return {
    ...replicateCounts,
    isFullyArchived: replicateCounts.liveReplicateCount === 0 && replicateCounts.archivedReplicateCount > 0,
  };
}

/** True when a condition's live replicate lanes are all staged for removal. */
export function isFullyPendingRemoved(
  liveReplicateCount: number,
  pendingRemovedReplicateCount: number
): boolean {
  return pendingRemovedReplicateCount > 0 && liveReplicateCount - pendingRemovedReplicateCount <= 0;
}

/** True when every live cell for this method is staged for removal. */
export function isConditionFullyPendingRemovedForMethod(
  condition: BaseCondition,
  conditionIndex: number,
  methodId: string,
  removedNodeIds: ReadonlySet<string>
): boolean {
  if (!isConditionGroup(condition) || removedNodeIds.size === 0) {
    return false;
  }
  const liveCellNodeIds = getConditionGroupGridRows(condition, [{id: methodId}], false).flatMap(
    ({replicateNumber, cells}) =>
      cells
        .filter((cell) => cell.methodId === methodId)
        .map(() =>
          getProcedureRunDesignFlowchartNodeId({
            conditionIndex,
            replicateIndex: replicateNumber - 1,
            methodId,
          })
        )
  );
  return liveCellNodeIds.length > 0 && liveCellNodeIds.every((nodeId) => removedNodeIds.has(nodeId));
}

/**
 * Count of replicate lanes fully staged for removal, keyed by condition index.
 *
 * A lane is counted only when every currently-live cell is in `removedNodeIds`, because removal is
 * method-scoped and an upstream cell can keep the lane live after save.
 */
export function countPendingRemovedReplicatesByConditionIndex(
  removedNodeIds: ReadonlySet<string>,
  conditions: ReadonlyArray<BaseCondition>,
  methods: ReadonlyArray<{id: string}>
): ReadonlyMap<number, number> {
  const countByConditionIndex = new Map<number, number>();
  if (removedNodeIds.size === 0) {
    return countByConditionIndex;
  }
  conditions.forEach((condition, conditionIndex) => {
    if (!isConditionGroup(condition)) {
      return;
    }
    let removedLaneCount = 0;
    for (const {replicateNumber, cells} of getConditionGroupGridRows(condition, methods, false)) {
      if (cells.length === 0) {
        continue;
      }
      const replicateIndex = replicateNumber - 1;
      const isLaneFullyRemoved = cells.every((cell) =>
        removedNodeIds.has(
          getProcedureRunDesignFlowchartNodeId({conditionIndex, replicateIndex, methodId: cell.methodId})
        )
      );
      if (isLaneFullyRemoved) {
        removedLaneCount += 1;
      }
    }
    if (removedLaneCount > 0) {
      countByConditionIndex.set(conditionIndex, removedLaneCount);
    }
  });
  return countByConditionIndex;
}

/**
 * A fully archived condition has a 0 replicate count. Preserve that 0 through unrelated table edits
 * so re-validation does not treat it as invalid and reset it with a spurious notification. Only
 * preserve when the row maps to an already-archived condition whose cell still reads 0, so editing a
 * live condition down to 0 still validates as before.
 */
export function keepFullyArchivedZeroReplicates(
  cell: ReplicateCountCell,
  isNewRow: boolean,
  originalCondition: BaseCondition | undefined
): boolean {
  if (isNewRow || originalCondition == null || originalCondition.numReplicates !== 0) {
    return false;
  }
  return (cell.text ?? '').trim() === '0';
}

export function getReplicateCellValue(cell: ReplicateCountCell): number | null {
  if (cell.text == null) {
    return null;
  }
  const value = Number.parseInt(cell.text, 10);
  if (Number.isNaN(value) || value <= 0) {
    notifications.error(
      `The number of replicates was reset to 1 since the specified value was not a whole number greater than 0`,
      {id: 'ReplicateTable-replicate-bounds'}
    );
    return 1;
  }
  if (value > EXPERIMENT_VALIDATION_CONSTANTS.MAX_REPLICATES_PER_CONDITION) {
    notifications.error(
      `The number of replicates was reset to ${EXPERIMENT_VALIDATION_CONSTANTS.MAX_REPLICATES_PER_CONDITION} since the specified value exceeded the maximum`,
      {
        id: 'ReplicateTable-replicate-bounds',
      }
    );
    return EXPERIMENT_VALIDATION_CONSTANTS.MAX_REPLICATES_PER_CONDITION;
  }
  return value;
}

/**
 * Resolves the replicate count for an edited table cell. A fully archived condition keeps its 0
 * count (see {@link keepFullyArchivedZeroReplicates}). Every other cell is validated and clamped by
 * {@link getReplicateCellValue}, defaulting to 1 when blank.
 */
export function resolveReplicateCount(
  numReplicatesCell: ReplicateCountCell,
  isNewRow: boolean,
  originalCondition: BaseCondition | undefined
): number {
  if (keepFullyArchivedZeroReplicates(numReplicatesCell, isNewRow, originalCondition)) {
    return 0;
  }
  return getReplicateCellValue(numReplicatesCell) ?? 1;
}

/**
 * Returns the condition name for a table row/column, falling back to a positional default name when
 * the name cell is blank.
 */
export function resolveConditionName(
  nameText: Optional<string>,
  index: number,
  procedureType: ProcedureType
): string {
  if (nameText == null || nameText === '') {
    return `${getProcedureRunConditionTextForProcedureType(procedureType, {capital: true})} ${index + 1}`;
  }
  return nameText;
}

export function makeValueCell(
  value: number,
  index: number,
  locked: boolean,
  isLockedPerCondition: Array<boolean>,
  formattedValue?: JSX.Element
): Cell {
  return {
    text: value.toString(),
    locked: locked || isLockedPerCondition[index],
    align: 'right',
    foregroundColor: NEAR_BLACK,
    bold: false,
    ...(formattedValue != null ? {formattedValue} : {}),
  };
}

export interface ConditionRowData extends RowData {
  // Keeps each transposed row tied to its original condition index for update/delete mapping.
  processDevelopmentCustomIndex?: number;
}

/** A condition draft paired with its original index, or null when newly added. */
export interface DraftedConditionRow {
  draft: ConditionDraft;
  originalIndex: number | null;
}

export function buildDraftsForRenderedRows<TCondition extends BaseCondition>(
  table: Table,
  conditions: Array<TCondition>
): Array<DraftedConditionRow> {
  let previousReplicateCount = 1;
  return table.table.map((row, index) => {
    const name = row[0].text;
    const repsText = row[1].text;
    const originalIndex = (table.rowData?.[index] as ConditionRowData)?.processDevelopmentCustomIndex ?? null;
    const isNewRow = originalIndex == null;
    const originalCondition = originalIndex == null ? undefined : conditions[originalIndex];

    // Default a new blank row from the preceding table row so consecutive pasted rows carry forward
    // the most recently specified replicate count.
    const isEmptyReplicates = repsText == null || repsText === '';
    const numReplicatesCell = {
      ...row[1],
      text: isNewRow && isEmptyReplicates ? Math.max(1, previousReplicateCount).toString() : repsText,
    };
    const numReplicates = resolveReplicateCount(numReplicatesCell, isNewRow, originalCondition);
    previousReplicateCount = numReplicates;

    return {
      draft: {
        numReplicates,
        name: name ?? '',
        description: row[2].text,
      },
      originalIndex,
    };
  });
}

/** Rebuilds the draft for a hidden condition so it survives table write-back. */
function reattachHiddenCondition(condition: BaseCondition): ConditionDraft {
  return {numReplicates: condition.numReplicates, name: condition.name, description: condition.description};
}

/**
 * Re-inserts hidden conditions into the write-back so saving preserves them. Each hidden row goes back
 * right after the visible row that originally came before it, or at the front when none did. Reorders
 * and inserts of the visible rows are preserved.
 */
function reinsertHiddenConditions<TCondition extends BaseCondition>(
  visibleRows: Array<DraftedConditionRow>,
  hiddenConditionIndices: ReadonlySet<number>,
  conditions: Array<TCondition>
): Array<DraftedConditionRow> {
  if (hiddenConditionIndices.size === 0) {
    return visibleRows;
  }

  const buildHiddenRow = (index: number): DraftedConditionRow => ({
    draft: reattachHiddenCondition(conditions[index]),
    originalIndex: index,
  });
  const visibleIndexes = visibleRows
    .map((row) => row.originalIndex)
    .filter((index): index is number => index != null)
    .sort((a, b) => a - b);

  const hiddenRowsBeforeAnyVisible: Array<DraftedConditionRow> = [];
  const hiddenRowsAfterVisibleIndex = new Map<number, Array<DraftedConditionRow>>();
  for (const hiddenIndex of [...hiddenConditionIndices].sort((a, b) => a - b)) {
    const visibleIndexBefore = visibleIndexes.findLast((index) => index < hiddenIndex);
    if (visibleIndexBefore == null) {
      hiddenRowsBeforeAnyVisible.push(buildHiddenRow(hiddenIndex));
    } else {
      const rowsAfter = hiddenRowsAfterVisibleIndex.get(visibleIndexBefore) ?? [];
      rowsAfter.push(buildHiddenRow(hiddenIndex));
      hiddenRowsAfterVisibleIndex.set(visibleIndexBefore, rowsAfter);
    }
  }

  return [
    ...hiddenRowsBeforeAnyVisible,
    ...visibleRows.flatMap((row) =>
      row.originalIndex == null ? [row] : [row, ...(hiddenRowsAfterVisibleIndex.get(row.originalIndex) ?? [])]
    ),
  ];
}

/**
 * Update conditions when any individual cell is updated.
 *
 * @package exported for testing.
 */
export function updateTableCells<TCondition extends BaseCondition>(
  table: Table,
  conditions: Array<TCondition>,
  setConditions: (conditions: Array<TCondition>, prevIndexes: Array<number | null>) => void,
  procedureType: ProcedureType,
  newConditionCoveredMethodIds?: ReadonlyArray<string>,
  hiddenConditionIndices?: ReadonlySet<number>
): void {
  const rows = reinsertHiddenConditions(
    buildDraftsForRenderedRows(table, conditions),
    hiddenConditionIndices ?? new Set(),
    conditions
  );
  const newConditions = rows.map((row, index) => ({
    ...row.draft,
    name: resolveConditionName(row.draft.name, index, procedureType),
  }));
  const originalValueIndexes = rows.map((row) => row.originalIndex);

  const updated = dispatchByConditionShape(
    conditions,
    (cs) => buildConditionsFromTableUpdate({newConditions, originalValueIndexes, conditions: cs}),
    (cs) =>
      buildConditionGroupsFromTableUpdate({
        newConditions,
        originalValueIndexes,
        conditions: cs,
        newConditionCoveredMethodIds,
      })
  );
  setConditions(updated as unknown as Array<TCondition>, originalValueIndexes);
}

export function getNameErrorTriangle(
  validationStatePerCondition: Array<ConditionValidationState>,
  index: number,
  procedureType: ProcedureType
): Optional<JSX.Element> {
  if (validationStatePerCondition[index].isNameLengthOverLimit) {
    return (
      <ErrorTriangle
        dataTestAttrs={{
          component: 'ReplicateTable',
          element: 'nameValidation',
          index: index.toString(),
        }}
        state={TriangleState.ILLEGAL}
        error={{
          type: '',
          message: `${getProcedureRunConditionTextForProcedureType(procedureType, {
            capital: true,
          })} name may not exceed ${EXPERIMENT_VALIDATION_CONSTANTS.CONDITION_NAME_MAX_LENGTH} characters`,
        }}
        location={TriangleLocation.CELL}
      />
    );
  } else if (validationStatePerCondition[index].isDuplicateName) {
    return (
      <ErrorTriangle
        dataTestAttrs={{
          component: 'ReplicateTable',
          element: 'nameValidation',
          index: index.toString(),
        }}
        state={TriangleState.ILLEGAL}
        error={{
          type: '',
          message: `${getProcedureRunConditionTextForProcedureType(procedureType, {
            capital: true,
          })} name already in use`,
        }}
        location={TriangleLocation.CELL}
      />
    );
  } else {
    return undefined;
  }
}

export function getDescErrorTriangle(
  validationStatePerCondition: Array<ConditionValidationState>,
  index: number,
  procedureType: ProcedureType
): Optional<JSX.Element> {
  if (validationStatePerCondition[index].isDescLengthOverLimit) {
    return (
      <ErrorTriangle
        dataTestAttrs={{
          component: 'ReplicateTable',
          element: 'descriptionValidation',
          index: index.toString(),
        }}
        state={TriangleState.ILLEGAL}
        error={{
          type: '',
          message: `${getProcedureRunConditionTextForProcedureType(procedureType, {
            capital: true,
          })} description may not exceed ${
            EXPERIMENT_VALIDATION_CONSTANTS.CONDITION_DESCRIPTION_MAX_LENGTH
          } characters`,
        }}
        location={TriangleLocation.CELL}
      />
    );
  } else {
    return undefined;
  }
}

/**
 * Asserts the list is homogeneous (all `Condition` or all `ConditionGroup`) and dispatches to
 * the matching builder. Centralizes the runtime shape check so callers stay generic at the
 * type level and mixed-shape lists fail loudly instead of silently picking a branch.
 *
 * Accepts the wider `BaseCondition` element type so that generic wrappers
 * (`<TCondition extends BaseCondition>`) can pass through without re-narrowing; the runtime
 * assertion is the real safety net regardless of the static type.
 *
 * Empty lists are treated as `Condition`-shaped by convention; both builders produce `[]` from
 * an empty input, so the choice is observationally identical.
 */
export function dispatchByConditionShape<TOut>(
  conditions: ReadonlyArray<BaseCondition>,
  onConditions: (conditions: Array<Condition>) => TOut,
  onGroups: (conditions: Array<ConditionGroup>) => TOut
): TOut {
  if (conditions.length === 0) {
    return onConditions([]);
  }
  const isGroups = isConditionGroup(conditions[0]);
  assert(
    conditions.every((c) => isConditionGroup(c) === isGroups),
    'ConditionGroup and Condition shapes must not be mixed in a single conditions list.'
  );
  return isGroups
    ? onGroups(conditions as unknown as Array<ConditionGroup>)
    : onConditions(conditions as unknown as Array<Condition>);
}

/**
 * Builds {@link Condition}-shaped rows from a `ReplicateTable` edit. Used by the new-run-
 * creation flow, whose conditions are unpersisted-by-construction (no ids); for each new row
 * mapped to an existing row via `originalValueIndexes`, we carry the existing row's optional
 * `id` through so persisted ids survive renames and reorders.
 */
export function buildConditionsFromTableUpdate({
  newConditions,
  originalValueIndexes,
  conditions,
}: {
  newConditions: Array<ConditionDraft>;
  originalValueIndexes: Array<number | null>;
  conditions: Array<Condition>;
}): Array<Condition> {
  const validationStates = buildValidationStatesForUpdate(newConditions);
  return newConditions.map((condition, i) => {
    const originalIndex = originalValueIndexes[i];
    const originalCondition = originalIndex != null ? conditions[originalIndex] : undefined;
    return {
      ...condition,
      id: originalCondition?.id,
      validationState: validationStates[i],
    };
  });
}

/**
 * Builds {@link ConditionGroup}-shaped rows from a `ReplicateTable` edit. Used by the
 * plan and existing-run design tabs; for each new row mapped to an existing row via
 * `originalValueIndexes`, the existing row's `conditionIdsByPmdvId` is carried through so
 * the persisted ↔ display-row linkage survives renames and reorders. Brand-new rows get an
 * empty map or a map with null condition IDs, which downstream save serializers consume as
 * "create new backend rows".
 *
 * The read-only per-cell archive data (`replicatesByConditionId`) is keyed by the same stable
 * persisted condition ids, so it is carried through unchanged alongside `conditionIdsByPmdvId`.
 * This keeps archived cells visible in the flowchart across renames/replicate-count edits without
 * ever entering the serialization path.
 */
export function buildConditionGroupsFromTableUpdate({
  newConditions,
  originalValueIndexes,
  conditions,
  newConditionCoveredMethodIds,
}: {
  newConditions: Array<ConditionDraft>;
  originalValueIndexes: Array<number | null>;
  conditions: Array<ConditionGroup>;
  newConditionCoveredMethodIds?: ReadonlyArray<string>;
}): Array<ConditionGroup> {
  const validationStates = buildValidationStatesForUpdate(newConditions);
  return newConditions.map((condition, i) => {
    const originalIndex = originalValueIndexes[i];
    const originalCondition = originalIndex != null ? conditions[originalIndex] : undefined;
    return {
      ...condition,
      conditionIdsByPmdvId:
        originalCondition?.conditionIdsByPmdvId ??
        buildDraftConditionGroupPmdvMap(newConditionCoveredMethodIds),
      ...(originalCondition?.replicatesByConditionId != null
        ? {replicatesByConditionId: originalCondition.replicatesByConditionId}
        : {}),
      validationState: validationStates[i],
    };
  });
}

export function confirmDeleteConditions({
  unitWord,
  conditionsToRemove,
  experimentalInputs,
  procedureType,
  isProcedureRunPlan,
  doDelete,
}: {
  unitWord: 'column' | 'row';
  conditionsToRemove: Array<BaseCondition>;
  experimentalInputs: ExperimentalInputs | null;
  procedureType: ProcedureType;
  isProcedureRunPlan: boolean;
  doDelete: () => void;
}): void {
  if (!isProcedureRunPlan) {
    // Only newly added draft condition lanes can be removed. Persisted conditions cannot be deleted.
    const persistedConditionsToRemove = conditionsToRemove.filter((condition) =>
      isPersistedCondition(condition)
    );
    if (persistedConditionsToRemove.length > 0) {
      notifications.error(
        `Cannot remove saved ${getProcedureRunConditionTextForProcedureType(procedureType, {
          plural: persistedConditionsToRemove.length > 1,
        })}.`
      );
      return;
    }
  }

  const numConditionsToRemove = conditionsToRemove.length;
  const singleConditionText = getProcedureRunConditionTextForProcedureType(procedureType);
  const conditionsText = pluralize(numConditionsToRemove, singleConditionText);
  const unitsText = pluralize(numConditionsToRemove, unitWord);
  const totalUnitOps = Object.keys(experimentalInputs ?? {}).length;
  const coveredMethodIdsForRemoval = dispatchByConditionShape(
    conditionsToRemove,
    // Non-ConditionGroup flows don't track method coverage, so default to all unit ops.
    () => null,
    (conditionGroups) => {
      const allExperimentalInputMethodIds = new Set(Object.keys(experimentalInputs ?? {}));
      const removedConditionCoveredMethodIds = conditionGroups.map(
        (conditionGroup) =>
          // An empty map is unspecified coverage of every unit operation, not a different method set.
          getMethodIdsImplementingConditionGroup(conditionGroup) ?? allExperimentalInputMethodIds
      );
      const firstCoveredMethodIds = removedConditionCoveredMethodIds[0] ?? null;
      removedConditionCoveredMethodIds.slice(1).forEach((coveredMethodIds) => {
        // Every removed row must cover the same method ids. New rows cover either all unit
        // operations or the active method plus downstream ones. Plan rows may already be saved.
        const isMatchingCoverage =
          (firstCoveredMethodIds == null && coveredMethodIds == null) ||
          (firstCoveredMethodIds != null &&
            coveredMethodIds != null &&
            coveredMethodIds.size === firstCoveredMethodIds.size &&
            [...coveredMethodIds].every((methodId) => firstCoveredMethodIds.has(methodId)));
        assert(isMatchingCoverage, 'Expected removed conditions to cover identical method ids', {
          removedConditionCoveredMethodIds,
        });
      });
      return firstCoveredMethodIds;
    }
  );
  // If coverage is unspecified (null), treat the removal as applying across all unit ops.
  const numUnitOps = coveredMethodIdsForRemoval?.size ?? totalUnitOps;

  modals.confirm(
    `Remove ${conditionsText}?`,
    <InlineBanner
      bannerType={InlineBannerType.ERROR}
      text={`${numConditionsToRemove} ${singleConditionText} ${unitsText} across ${numUnitOps} unit ${pluralize(
        numUnitOps,
        'operation'
      )} will be lost`}
    />,
    ButtonStyle.NEGATIVE,
    {
      acceptText: `Remove ${conditionsText}`,
      rejectText: 'Back to editing',
      enableRedesign: true,
      onSubmit: () => {
        doDelete();
      },
    }
  );
}
